import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import {
    MdEuro,
    MdOutlineAccountBalanceWallet,
    MdOutlineAccountBalance,
    MdArrowForward,
    MdInfoOutline,
    MdCheckCircle,
    MdHourglassEmpty,
} from 'react-icons/md';

type WalletTransaction = {
    _id?: string;
    amount: number;
    reason: string;
    createdAt: string;
};

type WithdrawalRequest = {
    _id?: string;
    amount: number;
    status: 'pending' | 'sent' | 'rejected';
    requestedAt: string;
    processedAt?: string;
    note?: string;
};

const fmt = (n: number) =>
    n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const statusLabel: Record<WithdrawalRequest['status'], string> = {
    pending: 'En attente',
    sent: 'Virement envoyé',
    rejected: 'Refusée',
};

// Shared primary CTA — keeps "Demander le retrait" and "Renseigner mon IBAN"
// visually identical (height, padding, typography, hover).
const PRIMARY_CTA =
    'inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-leadrs-brand px-5 font-geist text-sm font-medium text-white transition-colors hover:bg-leadrs-brand-light';

const SellerCagnotte = () => {
    const nav = useNavigate();
    const [walletBalance, setWalletBalance] = useState<number>(0);
    const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
    const [requests, setRequests] = useState<WithdrawalRequest[]>([]);
    const [paymentMethod, setPaymentMethod] = useState<string>('stripe');
    const [hasBankDetails, setHasBankDetails] = useState<boolean>(false);
    const [ibanLast4, setIbanLast4] = useState<string>('');
    const [loading, setLoading] = useState(true);

    const [amount, setAmount] = useState<string>('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const authHeaders = () => ({
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });

    const load = () => {
        void axios
            .get(`${CONFIG.apiUrl}/user/wallet`, authHeaders())
            .then((res) => {
                setWalletBalance(res.data.walletBalance || 0);
                setTransactions(res.data.walletTransactions || []);
                setRequests(res.data.walletWithdrawalRequests || []);
                setPaymentMethod(res.data.paymentMethod || 'stripe');
                setHasBankDetails(!!res.data.hasBankDetails);
                setIbanLast4(res.data.ibanLast4 || '');
            })
            .catch((err) => {
                console.error('Error fetching cagnotte:', err);
            })
            .finally(() => setLoading(false));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => load(), []);

    const pendingRequest = requests.find((r) => r.status === 'pending') || null;

    const isCagnotteMode = paymentMethod === 'cagnotte';
    const parsedAmount = useMemo(() => {
        const raw = amount.trim() === '' ? walletBalance : parseFloat(amount.replace(',', '.'));
        return Number.isFinite(raw) ? raw : NaN;
    }, [amount, walletBalance]);
    const amountValid = Number.isFinite(parsedAmount) && parsedAmount > 0 && parsedAmount <= walletBalance + 0.001;
    const canRequest = isCagnotteMode && hasBankDetails && walletBalance > 0 && !pendingRequest;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        const parsed = amount.trim() === '' ? walletBalance : parseFloat(amount.replace(',', '.'));
        if (!Number.isFinite(parsed) || parsed <= 0) {
            setError('Montant invalide.');
            return;
        }
        if (parsed > walletBalance) {
            setError('Le montant demandé dépasse le solde de votre cagnotte.');
            return;
        }

        setSubmitting(true);
        try {
            const res = await axios.post(
                `${CONFIG.apiUrl}/user/wallet/withdrawal-request`,
                { amount: parsed },
                authHeaders()
            );
            setSuccess(
                `Demande de retrait de ${fmt(res.data.amount)} envoyée. Ce montant a été déduit de votre solde disponible et placé en cours de traitement. Il vous sera versé après validation, ou recrédité si la demande est refusée.`
            );
            setAmount('');
            load();
        } catch (err: any) {
            setError(err?.response?.data?.error || 'Erreur lors de la demande de retrait.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="flex flex-col gap-4">
            {/* Header */}
            <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-leadrs-brown-soft">
                        <MdOutlineAccountBalanceWallet className="h-5 w-5 text-leadrs-brown-focus" />
                    </div>
                    <div>
                        <h2 className="font-crimson text-2xl font-semibold leading-tight text-leadrs-text">
                            Ma cagnotte
                        </h2>
                        <p className="mt-1 font-geist text-sm text-leadrs-text-soft">
                            Le solde de votre cagnotte correspond aux paiements de vos leads. Vous pouvez demander
                            un virement à tout moment.
                        </p>
                    </div>
                </div>
            </div>

            {/* Balance */}
            <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                <div className="flex items-center gap-4">
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-leadrs-yellow-soft">
                        <MdEuro className="h-5 w-5 text-leadrs-yellow-focus" />
                    </div>
                    <div>
                        <p className="font-geist text-sm text-leadrs-text-soft">Solde disponible</p>
                        <p className="font-crimson text-3xl font-semibold text-leadrs-text">
                            {loading ? '—' : fmt(walletBalance)}
                        </p>
                        {!loading && pendingRequest && (
                            <p className="mt-1 font-geist text-xs text-leadrs-text-soft">
                                {fmt(pendingRequest.amount)} en cours de retrait, déjà déduits de ce solde.
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Withdrawal request */}
            <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                <p className="mb-1 font-crimson text-xl font-semibold text-leadrs-text">
                    Demander un retrait
                </p>
                <p className="mb-4 font-geist text-sm text-leadrs-text-soft">
                    Le virement est effectué manuellement par notre équipe vers le compte bancaire enregistré
                    dans vos informations de facturation.
                </p>

                {loading ? (
                    <div className="h-24 animate-pulse rounded-lg bg-leadrs-bg" />
                ) : !isCagnotteMode ? (
                    <div className="flex items-start gap-3 rounded-lg bg-leadrs-yellow-soft px-4 py-3">
                        <MdInfoOutline className="mt-0.5 h-5 w-5 flex-shrink-0 text-leadrs-yellow-focus" />
                        <p className="font-geist text-sm leading-relaxed text-leadrs-yellow-focus">
                            Votre compte est actuellement payé directement via Stripe. Contactez le support si
                            vous souhaitez passer en mode cagnotte.
                        </p>
                    </div>
                ) : !hasBankDetails ? (
                    /* CTA — no IBAN on file yet */
                    <div className="flex flex-col items-start gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-leadrs-yellow-soft">
                            <MdOutlineAccountBalance className="h-5 w-5 text-leadrs-yellow-focus" />
                        </div>
                        <div>
                            <h3 className="font-crimson text-lg font-semibold leading-tight text-leadrs-text">
                                Ajoutez vos coordonnées bancaires
                            </h3>
                            <p className="mt-1 max-w-md font-geist text-sm leading-relaxed text-leadrs-text-soft">
                                Renseignez votre IBAN dans vos informations de facturation pour pouvoir demander
                                un virement de votre cagnotte.
                            </p>
                        </div>
                        <button
                            onClick={() => nav('/admin/settings?tab=invoice&from=cagnotte')}
                            className={`${PRIMARY_CTA} mt-1`}
                        >
                            Renseigner mon IBAN
                            <MdArrowForward className="h-4 w-4" />
                        </button>
                    </div>
                ) : pendingRequest ? (
                    <div className="flex items-start gap-3 rounded-lg bg-leadrs-brown-soft px-4 py-3">
                        <MdHourglassEmpty className="mt-0.5 h-5 w-5 flex-shrink-0 text-leadrs-brown-focus" />
                        <div>
                            <p className="font-geist text-sm font-medium text-leadrs-brown-focus">
                                Demande de {fmt(pendingRequest.amount)} en cours de traitement
                            </p>
                            <p className="mt-0.5 font-geist text-sm leading-relaxed text-leadrs-brown-focus">
                                Envoyée le{' '}
                                {new Date(pendingRequest.requestedAt).toLocaleDateString('fr-FR')}. Ce montant a
                                été déduit de votre solde disponible le temps du traitement. Le virement sera
                                effectué sous quelques jours ouvrés
                                {ibanLast4 ? ` vers l'IBAN se terminant par ••${ibanLast4}` : ''}. Si la demande
                                est refusée, le montant est recrédité sur votre cagnotte.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        {ibanLast4 && (
                            <div className="flex items-center gap-2 rounded-lg bg-leadrs-bg px-3 py-2 font-geist text-xs text-leadrs-text-soft">
                                <MdCheckCircle className="h-4 w-4 text-leadrs-green-focus" />
                                Virement vers l'IBAN se terminant par{' '}
                                <span className="font-semibold text-leadrs-text">••{ibanLast4}</span>
                                <button
                                    onClick={() => nav('/admin/settings?tab=invoice&from=cagnotte')}
                                    className="ml-1 underline hover:text-leadrs-text"
                                >
                                    modifier
                                </button>
                            </div>
                        )}
                        <form onSubmit={handleSubmit} className="flex flex-col gap-2">
                            <label className="font-geist text-sm font-medium text-leadrs-text">
                                Montant à retirer
                            </label>
                            <div className="flex flex-wrap items-center gap-2">
                                <div className="relative">
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max={walletBalance}
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        placeholder={walletBalance.toFixed(2)}
                                        className="h-11 w-40 rounded-lg border border-leadrs-stroke bg-white pl-4 pr-8 font-geist text-sm text-leadrs-text focus:border-leadrs-brand focus:outline-none"
                                    />
                                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 font-geist text-sm text-leadrs-text-soft">
                                        €
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setAmount(walletBalance.toFixed(2))}
                                    className="inline-flex h-11 items-center rounded-lg border border-leadrs-stroke px-3 font-geist text-sm font-medium text-leadrs-text-soft transition-colors hover:border-leadrs-brand hover:text-leadrs-text"
                                >
                                    Tout retirer
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting || !canRequest || !amountValid}
                                    className={`${PRIMARY_CTA} disabled:cursor-not-allowed disabled:bg-leadrs-disabled disabled:text-leadrs-text-soft disabled:hover:bg-leadrs-disabled`}
                                >
                                    {submitting ? 'Envoi...' : 'Demander le retrait'}
                                </button>
                            </div>
                            <p className="font-geist text-xs text-leadrs-text-soft">
                                Laissez vide pour retirer la totalité ({fmt(walletBalance)}).
                            </p>
                        </form>
                    </div>
                )}

                {error && (
                    <div className="mt-3 rounded-lg bg-leadrs-red-soft px-4 py-3 font-geist text-sm text-leadrs-red-focus">
                        {error}
                    </div>
                )}
                {success && (
                    <div className="mt-3 rounded-lg bg-leadrs-green-soft px-4 py-3 font-geist text-sm text-leadrs-green-focus">
                        {success}
                    </div>
                )}
            </div>

            {/* Withdrawal requests history */}
            {requests.length > 0 && (
                <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                    <p className="mb-4 font-crimson text-xl font-semibold text-leadrs-text">
                        Historique des demandes de retrait
                    </p>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-leadrs-stroke">
                                    {['Date', 'Montant', 'Statut'].map((h) => (
                                        <th
                                            key={h}
                                            className="pb-3 text-left font-geist text-xs font-semibold uppercase tracking-wide text-leadrs-text-soft"
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {requests.map((r, i) => (
                                    <tr key={r._id || i} className="border-b border-leadrs-stroke last:border-0">
                                        <td className="py-3 font-geist text-sm text-leadrs-text-soft">
                                            {new Date(r.requestedAt).toLocaleDateString('fr-FR')}
                                        </td>
                                        <td className="py-3 font-geist text-sm font-semibold text-leadrs-text">
                                            {fmt(r.amount)}
                                        </td>
                                        <td className="py-3 font-geist text-sm text-leadrs-text-soft">
                                            {statusLabel[r.status]}
                                            {r.status === 'rejected' && r.note ? ` — ${r.note}` : ''}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Transaction history */}
            <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                <p className="mb-4 font-crimson text-xl font-semibold text-leadrs-text">
                    Historique des transactions
                </p>

                {loading ? (
                    <p className="text-center font-geist text-sm text-leadrs-text-soft">Chargement...</p>
                ) : transactions.length === 0 ? (
                    <p className="text-center font-geist text-sm text-leadrs-text-soft">
                        Aucune transaction pour le moment.
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-leadrs-stroke">
                                    {['Date', 'Montant', 'Raison'].map((h) => (
                                        <th
                                            key={h}
                                            className="pb-3 text-left font-geist text-xs font-semibold uppercase tracking-wide text-leadrs-text-soft"
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.map((tx, i) => (
                                    <tr key={tx._id || i} className="border-b border-leadrs-stroke last:border-0">
                                        <td className="py-3 font-geist text-sm text-leadrs-text-soft">
                                            {new Date(tx.createdAt).toLocaleDateString('fr-FR')}
                                        </td>
                                        <td className="py-3">
                                            <span
                                                className={`font-geist text-sm font-semibold ${
                                                    tx.amount >= 0 ? 'text-leadrs-success' : 'text-leadrs-text'
                                                }`}
                                            >
                                                {tx.amount >= 0 ? '+' : ''}
                                                {tx.amount.toFixed(2)} €
                                            </span>
                                        </td>
                                        <td className="py-3 font-geist text-sm text-leadrs-text-soft">
                                            {tx.reason || '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SellerCagnotte;
